'use client'

import Link from 'next/link'

import { KineticWorkshopFooter } from '../../../components/kinetic/KineticWorkshopFooter'
import { Button } from '../../../components/ui/button'

const curriculum = [
  {
    day: 'Day 1: Modern Architecture',
    topics: [
      'Traditional vs Modern Architecture',
      'Data Mesh & Data Fabric',
      'Infrastructure as Code (Terraform)',
      'GitOps principles',
    ],
  },
  {
    day: 'Day 2: Data Ingestion',
    topics: [
      'Modern ELT patterns',
      'Change Data Capture (CDC)',
      'Streaming with Kafka/Kinesis',
      'Modern orchestration with Mage',
    ],
  },
  {
    day: 'Day 3: Modern Storage',
    topics: [
      'Modern Data Warehouses',
      'Data Lakes & Lake Houses',
      'Storage optimisation',
      'Query performance tuning',
    ],
  },
  {
    day: 'Day 4: Real-Time Processing',
    topics: [
      'Stream processing fundamentals',
      'Clickhouse Streaming',
      'Real-time analytics patterns',
      'Visualising streams with Streamlit',
    ],
  },
  {
    day: 'Day 5: Production Ready',
    topics: [
      'CI/CD for data pipelines',
      'Security best practices',
      'Cost Optimisation',
      'Production Deployment',
    ],
  },
]

const benefits = [
  'Transform traditional ETL skills into modern ELT practices',
  'Learn cloud-native architectures and infrastructure as code',
  'Master modern tools like dbt, Airflow, and streaming technologies',
  'Implement real-time processing and modern data warehousing',
]

const features = [
  { title: 'Career Growth', desc: 'Position yourself for senior roles with modern skills' },
  { title: 'Cloud Expertise', desc: 'Become proficient in cloud-native architectures' },
  { title: 'Modern Stack', desc: 'Master the tools that modern companies use' },
]

export default function ModernDataEngWorkshopPage() {
  return (
    <div className="min-h-screen bg-kinetic-surface text-kinetic-on-surface">
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-24 sm:px-12 lg:px-16">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl flex-1">
            <h1 className="font-headline text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">
              Modernise Your Data Engineering Skills
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-kinetic-on-surface-variant">
              Bridge the gap between traditional and modern cloud-native data engineering in 5 days.
            </p>
            <div className="mt-8">
              <Link href="/workshops/register">
                <Button variant="kinetic" size="lg">
                  Secure Your Spot
                </Button>
              </Link>
            </div>
          </div>
          <div className="border-kinetic-outline-variant/30 flex-shrink-0 border bg-kinetic-surface-container-low p-7 lg:w-80">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-kinetic-on-surface-variant">
              5-Day Intensive
            </p>
            <p className="font-headline text-3xl font-bold text-white">$2,499</p>
            <p className="mt-2 text-sm text-kinetic-on-surface-variant">
              Virtual (Live) · Advanced
            </p>
            <Link href="/workshops/register" className="mt-4 block">
              <Button variant="kinetic" size="lg" className="w-full">
                Reserve Your Spot
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="mx-auto max-w-7xl px-6 pb-16 sm:px-12 lg:px-16">
        <h2 className="mb-8 font-headline text-2xl font-bold uppercase tracking-tight text-white">
          Feel Stuck with Legacy Systems?
        </h2>
        <div className="space-y-4">
          {benefits.map(b => (
            <div key={b} className="flex items-start gap-4">
              <div className="mt-0.5 h-6 w-6 flex-shrink-0 rounded-full bg-kinetic-primary-container" />
              <p className="leading-relaxed text-kinetic-on-surface-variant">{b}</p>
            </div>
          ))}
        </div>
        <Link href="/workshops/register" className="mt-8 inline-block">
          <Button variant="kinetic" size="lg">
            Reserve Your Spot Now
          </Button>
        </Link>
      </section>

      {/* Curriculum */}
      <section className="mx-auto max-w-7xl px-6 pb-16 sm:px-12 lg:px-16">
        <h2 className="mb-8 font-headline text-2xl font-bold uppercase tracking-tight text-white">
          Your 5-Day Transformation Journey
        </h2>
        <div className="space-y-4">
          {curriculum.map(item => (
            <div
              key={item.day}
              className="border-kinetic-outline-variant/30 border bg-kinetic-surface-container-low p-6"
            >
              <h3 className="mb-3 font-headline text-base font-semibold text-white">{item.day}</h3>
              <ul className="space-y-2">
                {item.topics.map(t => (
                  <li
                    key={t}
                    className="flex items-center gap-3 text-sm text-kinetic-on-surface-variant"
                  >
                    <div className="h-1.5 w-1.5 rounded-full bg-kinetic-primary-container" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <Link href="/workshops/register" className="mt-8 inline-block">
          <Button variant="kinetic" size="lg">
            Reserve Your Spot Now
          </Button>
        </Link>
      </section>

      {/* Features */}
      <section className="border-kinetic-outline-variant/30 border-t bg-kinetic-surface-container-low py-16">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-16">
          <h2 className="mb-12 text-center font-headline text-2xl font-bold uppercase tracking-tight text-white">
            Investment In Your Future
          </h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {features.map(f => (
              <div
                key={f.title}
                className="border-kinetic-outline-variant/30 border bg-kinetic-surface-container-lowest p-6 text-center"
              >
                <div className="mx-auto mb-4 h-12 w-12 bg-kinetic-primary-container" />
                <h3 className="font-headline text-lg font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-sm text-kinetic-on-surface-variant">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-16 text-center sm:px-12 lg:px-16">
        <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white">
          Ready to Modernise Your Skills?
        </h2>
        <p className="mx-auto mt-2 max-w-md text-base text-kinetic-on-surface-variant">
          Join other professionals making the leap to modern data engineering.
        </p>
        <Link href="/workshops/register" className="mt-6 inline-block">
          <Button variant="kinetic" size="lg">
            Enroll Now
          </Button>
        </Link>
      </section>

      <KineticWorkshopFooter />
    </div>
  )
}
